import React, { CSSProperties, FC, ReactNode, useEffect, useMemo, useState } from "react";
import {
    DataTable,
    DataTableBody,
    DataTableContent,
    DataTableHead,
    DataTableHeadCell,
    DataTableHeadCellProps,
    DataTableProps,
    DataTableRow,
} from "@rmwc/data-table";
import "@rmwc/data-table/data-table.css";
import { Row } from "./Row";
import { getZIndex, RowModel } from "./utils";
import { OrderByType } from "components/OrgOverview/containers/OrgOverviewContainer";
import { LoadingSpinner, IconButton } from "@xapp/components";
import style from "./style.scss";
import { MatchConfidenceHeaderView } from "components/AppRawQuery/components/MatchConfidenceHeaderView";
/**
 * Columns can either be built using strings (via the label prop)
 * or components via the children prop
 *
 * @export
 * @interface TableColumn
 * @extends {DataTableHeadCellProps}
 */
export interface TableColumn extends DataTableHeadCellProps {
    id: string;
    label?: string;
}

/**
 * See https://rmwc.io/data-tables for a full list of configuration options
 *
 * @export
 * @interface TableProps
 * @extends {DataTableProps}
 */
export interface TableProps extends DataTableProps {
    readonly columns: TableColumn[];
    readonly rows: RowModel[];
    readonly loading?: boolean;
    gtConfidence?: number;
    ltConfidence?: number;
    paginationUrl?: string
    search?(key: string): void;
    baseURL?: string;
    onSortClick?: (value: OrderByType) => void;
    sortValue?: OrderByType;
    children?: ReactNode;
}

const TableView: FC<TableProps> = ({
    columns,
    rows,
    loading,
    gtConfidence,
    ltConfidence,
    paginationUrl,
    baseURL,
    stickyColumns,
    stickyRows,
    search,
    sortValue,
    onSortClick,
    children,
    ...tableProps
}: TableProps) => {

    const [currentSortValue, setCurrentSortValue] = useState<OrderByType>(sortValue);

    const trs = useMemo(() => {
        return rows?.map((row) => {
            let prioritize = false;
            if (row.cells.length > 0) {
                const content: JSX.Element = row.cells[0].cellContent as JSX.Element;
                const rowNotes: string = content?.props?.notes;
                prioritize = rowNotes?.toLowerCase().includes("prioritize");
            }
            return <Row key={row.key} cells={row.cells} search={search} prioritize={prioritize} />;
        });
    }, [rows, search]);

    useEffect(() => {
        if (onSortClick) {
            onSortClick(currentSortValue);
        }
    }, [currentSortValue, onSortClick])

    const handleMenuItemClick = (column: string) => {
        switch (column) {
            case "Last modified":
                if (currentSortValue === OrderByType.DATE_ASC) {
                    setCurrentSortValue(OrderByType.DATE_DESC)
                } else {
                    setCurrentSortValue(OrderByType.DATE_ASC)
                }
                break;
            case "App Name":
                if (currentSortValue === OrderByType.NAME_ASC) {
                    setCurrentSortValue(OrderByType.NAME_DESC)
                } else {
                    setCurrentSortValue(OrderByType.NAME_ASC)
                }
                break;
            default:
                setCurrentSortValue(OrderByType.DATE_DESC)
        }
    };

    return (
        <>
            <DataTable className={style.wrap} {...tableProps}>
                <DataTableContent>
                    <DataTableHead>
                        <DataTableRow>
                            {columns.map((column: TableColumn, i: number) => {
                                const { id, label, ...headCellProps } = column;

                                // the z-index is being manually set here to avoid some very specific code coming from rmwc
                                const headCellStyle: CSSProperties = {
                                    zIndex: getZIndex(stickyColumns >= 1 || stickyRows >= 1, i),
                                };

                                return (
                                    <>
                                        <DataTableHeadCell style={headCellStyle} key={column.id} {...headCellProps}>
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                {column.children ? column.children : column.label}
                                                {(column.label === "Last modified") && (
                                                    <IconButton iconName={currentSortValue === OrderByType.DATE_ASC ? `arrow_upward` : `arrow_downward`} onClick={() => handleMenuItemClick(column.label)} />
                                                )}
                                                {(column.label === "App Name") && (
                                                    <IconButton iconName={currentSortValue === OrderByType.NAME_ASC ? `arrow_upward` : `arrow_downward`} onClick={() => handleMenuItemClick(column.label)} />
                                                )}
                                            </div>
                                            {column.label === "Match Confidence" && (
                                                <MatchConfidenceHeaderView
                                                    gtConfidence={gtConfidence}
                                                    ltConfidence={ltConfidence}
                                                    paginationUrl={paginationUrl} />
                                            )}

                                        </DataTableHeadCell>
                                    </>
                                );
                            })}
                        </DataTableRow>
                    </DataTableHead>

                    <DataTableBody>{!loading && trs}</DataTableBody>
                </DataTableContent>
            </DataTable>
            {loading && <LoadingSpinner />}
        </>
    );
};

export { TableView };
